import { useMemo, useState } from "react";
import { api, formatApiErrorDetail, getBackendUrl } from "@/lib/api";
import { toast } from "sonner";
import {
  Play, FilePdf, Trash, ArrowsHorizontal, Eye, ArrowClockwise, GitDiff, X, ChartLine, FileArchive, Share,
} from "@phosphor-icons/react";

/* v2.96 · Studies panel for the Project Builder.
   Lists every saved study attached to a project and gives the user
   four affordances per row:
     • view     — pretty-print inputs + result
     • re-run   — POST the saved inputs to /api/studies/<kind> and
                  patch the row's `result` in place.
     • export   — POST /api/studies/export-pdf and download the blob.
     • delete   — DELETE /api/studies/project/{pid}/{sid}
   Plus a top-level "compare two runs" mode: select two studies →
   side-by-side JSON diff (line-based, matching key path). */

const KIND_LABEL = {
  load_flow:      "Load flow",
  short_circuit:  "Short-circuit",
  motor_starting: "Motor starting",
  tcc:            "TCC coord.",
  harmonics:      "Harmonics",
  battery_sizing: "Battery",
  grounding:      "Grounding",
};
const KIND_ENDPOINT = {
  load_flow:      "/studies/load-flow",
  short_circuit:  "/studies/short-circuit",
  motor_starting: "/studies/motor-starting",
  tcc:            "/studies/tcc",
  harmonics:      "/studies/harmonics",
  battery_sizing: "/studies/battery-sizing",
  grounding:      "/studies/grounding",
};

// Flat "path → value" pairs so we can diff two nested payloads.
function flatten(obj, prefix = "") {
  const out = [];
  if (obj === null || obj === undefined) return out;
  if (typeof obj !== "object") return [[prefix || "value", obj]];
  if (Array.isArray(obj)) {
    obj.forEach((v, i) => out.push(...flatten(v, `${prefix}[${i}]`)));
    return out;
  }
  for (const [k, v] of Object.entries(obj)) {
    out.push(...flatten(v, prefix ? `${prefix}.${k}` : k));
  }
  return out;
}

function DiffView({ a, b }) {
  const rows = useMemo(() => {
    const av = new Map(flatten(a));
    const bv = new Map(flatten(b));
    const keys = new Set([...av.keys(), ...bv.keys()]);
    return Array.from(keys).sort().map((k) => ({
      key: k, a: av.get(k), b: bv.get(k),
      same: JSON.stringify(av.get(k)) === JSON.stringify(bv.get(k)),
    }));
  }, [a, b]);
  return (
    <div className="border border-white/10 bg-[#0A0C10] font-mono text-[11px]" data-testid="studies-diff">
      <div className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-white/10 text-gray-400 uppercase text-[9px] tracking-widest">
        <div className="col-span-4">key</div>
        <div className="col-span-4">run A</div>
        <div className="col-span-4">run B</div>
      </div>
      <div className="max-h-[500px] overflow-auto">
        {rows.map((r) => (
          <div key={r.key}
            className={`grid grid-cols-12 gap-2 px-3 py-1 border-b border-white/5 ${
              r.same ? "text-gray-500" : "text-white bg-[#FF3B30]/10"}`}>
            <div className="col-span-4 truncate" title={r.key}>{r.key}</div>
            <div className="col-span-4 truncate">{fmt(r.a)}</div>
            <div className="col-span-4 truncate">{fmt(r.b)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
function fmt(v) {
  if (v === undefined) return "—";
  if (typeof v === "number") return v.toFixed ? Number(v).toString() : String(v);
  return typeof v === "object" ? JSON.stringify(v) : String(v);
}

export default function StudiesTab({ project, projectId, update }) {
  const studies = project?.studies || [];
  const [viewOne, setViewOne] = useState(null);
  const [busy, setBusy] = useState(null);            // study.id currently busy
  const [compareOn, setCompareOn] = useState(false);
  const [picked, setPicked] = useState([]);          // [id, id] max 2

  const reload = async () => {
    const { data } = await api.get(`/projects/${projectId}`);
    update?.(data);
  };

  const rerun = async (s) => {
    const endpoint = KIND_ENDPOINT[s.kind];
    if (!endpoint) { toast.error(`No re-run path for ${s.kind}`); return; }
    setBusy(s.id);
    try {
      const { data } = await api.post(endpoint, s.inputs);
      // Save updated result to project.studies (overwrites by name+kind).
      await api.post("/studies/save-to-project", {
        project_id: projectId, name: s.name, kind: s.kind,
        inputs: s.inputs, result: data,
      });
      toast.success(`${s.name} re-run · saved`);
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Re-run failed");
    } finally { setBusy(null); }
  };

  const exportPdf = async (s) => {
    try {
      const res = await fetch(`${getBackendUrl()}/api/studies/export-pdf`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: s.name, kind: s.kind, inputs: s.inputs, result: s.result,
          project_name: project?.name,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `study_${s.name.replace(/\W+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF exported");
    } catch (e) {
      toast.error("PDF export failed");
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete ${JSON.stringify(s.name)}? This can't be undone.`)) return;
    try {
      await api.delete(`/studies/project/${projectId}/${s.id}`);
      toast.success("Deleted");
      await reload();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  // v2.98 · One-click "Compliance Bundle" download — assembles every
  // saved study into a single PE-stampable PDF (cover, insights,
  // remediation notes, per-study calc sheets).
  // v3.0 · Client Deliverables Portal — mint a signed share link.
  const share = async () => {
    try {
      const { data } = await api.post(`/projects/${projectId}/share-link`, null, { params: { days: 30 } });
      const full = `${window.location.origin}${data.url}`;
      await navigator.clipboard.writeText(full);
      toast.success("Portal link copied · valid 30 days");
      window.open(full, "_blank");
    } catch {
      toast.error("Share link failed");
    }
  };

  const downloadBundle = async () => {
    try {
      const res = await fetch(`${getBackendUrl()}/api/studies/compliance-bundle/${projectId}`, {
        method: "POST", credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `compliance_bundle_${(project?.name || "project").replace(/\W+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Compliance bundle downloaded");
    } catch (e) {
      toast.error("Bundle export failed");
    }
  };

  const togglePick = (id) => {
    setPicked((prev) => prev.includes(id) ? prev.filter((x) => x !== id)
      : (prev.length >= 2 ? [prev[1], id] : [...prev, id]));
  };
  const pickedStudies = picked.map((id) => studies.find((s) => s.id === id)).filter(Boolean);

  if (studies.length === 0) return (
    <div className="p-10 text-center text-gray-500 border border-white/10 bg-[#12151C]/60"
         data-testid="studies-empty">
      <ChartLine size={32} className="mx-auto text-[#00E5FF]/40" />
      <div className="font-display text-2xl text-gray-300 mt-3">No studies saved yet</div>
      <div className="text-sm mt-2">
        Head to <a href="/studies" className="text-[#00E5FF] hover:underline">Studies</a>,
        run any analysis, then use the &quot;save&quot; action on the result card
        to attach it to this project.
      </div>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="studies-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="label-tag">// project · studies</div>
          <h3 className="font-display text-2xl font-bold mt-0.5">
            {studies.length} saved {studies.length === 1 ? "study" : "studies"}
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={share}
            className="text-[10px] font-mono px-3 py-1.5 border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/10 inline-flex items-center gap-1"
            data-testid="studies-share-btn"
            title="Mint a read-only 30-day share link for your client">
            <Share size={12} weight="fill" /> share with client
          </button>
          <button onClick={downloadBundle}
            className="text-[10px] font-mono px-3 py-1.5 border border-[#FFCC00]/40 text-[#FFCC00] hover:bg-[#FFCC00]/10 inline-flex items-center gap-1"
            data-testid="studies-bundle-btn"
            title="Assemble all studies + insights into a single PE-stampable PDF">
            <FileArchive size={12} weight="fill" /> compliance bundle
          </button>
          <button onClick={() => { setCompareOn(!compareOn); setPicked([]); }}
            className={`text-[10px] font-mono px-3 py-1.5 border ${compareOn ? "border-[#00E5FF] text-[#00E5FF]" : "border-white/10 text-gray-400"} hover:text-white`}
            data-testid="studies-compare-toggle">
            <GitDiff size={12} className="inline mr-1" />
            {compareOn ? "cancel compare" : "compare two runs"}
          </button>
          <a href="/studies" className="btn-primary text-xs inline-flex items-center gap-1"
             data-testid="studies-open-studio">
            <Play size={12} weight="fill" /> Open Studies
          </a>
        </div>
      </div>

      {compareOn && (
        <div className="text-[10px] font-mono text-[#00E5FF] p-2 border border-[#00E5FF]/30 bg-[#00E5FF]/5">
          Pick two studies below — {picked.length}/2 selected.
        </div>
      )}

      <div className="border border-white/10 divide-y divide-white/5">
        {studies.map((s) => {
          const isPicked = picked.includes(s.id);
          return (
          <div key={s.id}
            className={`grid grid-cols-12 gap-3 items-center p-3 font-mono text-xs hover:bg-white/[0.02] ${isPicked ? "bg-[#00E5FF]/10" : ""}`}
            data-testid={`studies-row-${s.id}`}>
            {compareOn && (
              <div className="col-span-1">
                <input type="checkbox" checked={isPicked}
                  onChange={() => togglePick(s.id)}
                  data-testid={`studies-pick-${s.id}`}
                  className="accent-[#00E5FF]" />
              </div>
            )}
            <div className={compareOn ? "col-span-4" : "col-span-5"}>
              <div className="text-white font-bold text-sm">{s.name}</div>
              <div className="text-gray-500 text-[10px] uppercase tracking-widest">
                {KIND_LABEL[s.kind] || s.kind}
              </div>
            </div>
            <div className="col-span-3 text-gray-500">
              {s.saved_at ? new Date(s.saved_at).toLocaleString() : "—"}
            </div>
            <div className="col-span-2 text-gray-500">
              {s.result ? <span className="text-[#7CFF6B]">✓ has result</span>
                        : <span className="text-[#FFCC00]">no result yet</span>}
            </div>
            <div className="col-span-2 flex items-center justify-end gap-1">
              <button onClick={() => setViewOne(s)}
                className="p-1.5 hover:bg-white/5 text-gray-400 hover:text-white"
                data-testid={`studies-view-${s.id}`} title="View">
                <Eye size={13} />
              </button>
              <button onClick={() => rerun(s)}
                disabled={busy === s.id}
                className="p-1.5 hover:bg-white/5 text-gray-400 hover:text-[#00E5FF] disabled:opacity-30"
                data-testid={`studies-rerun-${s.id}`} title="Re-run">
                <ArrowClockwise size={13} className={busy === s.id ? "animate-spin" : ""} />
              </button>
              <button onClick={() => exportPdf(s)}
                className="p-1.5 hover:bg-white/5 text-gray-400 hover:text-white"
                data-testid={`studies-pdf-${s.id}`} title="Export PDF">
                <FilePdf size={13} />
              </button>
              <button onClick={() => remove(s)}
                className="p-1.5 hover:bg-white/5 text-gray-400 hover:text-[#FF3B30]"
                data-testid={`studies-delete-${s.id}`} title="Delete">
                <Trash size={13} />
              </button>
            </div>
          </div>
        );})}
      </div>

      {compareOn && pickedStudies.length === 2 && (
        <div className="mt-4">
          <div className="flex items-center gap-2 mb-2">
            <ArrowsHorizontal size={14} className="text-[#00E5FF]" />
            <span className="font-mono text-xs text-gray-300">
              <b className="text-white">{pickedStudies[0].name}</b> vs
              <b className="text-white ml-1">{pickedStudies[1].name}</b> — differences highlighted in red
            </span>
          </div>
          <DiffView a={pickedStudies[0].result || pickedStudies[0].inputs}
                    b={pickedStudies[1].result || pickedStudies[1].inputs} />
        </div>
      )}

      {viewOne && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6"
             onClick={() => setViewOne(null)} data-testid="studies-view-modal">
          <div className="w-full max-w-4xl bg-[#12151C] border border-white/10 p-5"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="label-tag">// {KIND_LABEL[viewOne.kind] || viewOne.kind}</div>
                <h3 className="font-display text-xl font-bold mt-0.5">{viewOne.name}</h3>
                <div className="text-[10px] font-mono text-gray-500">saved {new Date(viewOne.saved_at).toLocaleString()}</div>
              </div>
              <button onClick={() => setViewOne(null)}
                className="text-gray-400 hover:text-white"
                data-testid="studies-view-close"><X size={16} /></button>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <div>
                <div className="label-tag">// inputs</div>
                <pre className="mt-1 border border-white/10 bg-[#0A0C10] p-3 font-mono text-[11px] leading-relaxed text-gray-200 max-h-[400px] overflow-auto">
                  {JSON.stringify(viewOne.inputs, null, 2)}
                </pre>
              </div>
              <div>
                <div className="label-tag">// result</div>
                <pre className="mt-1 border border-[#00E5FF]/30 bg-[#0A0C10] p-3 font-mono text-[11px] leading-relaxed text-gray-200 max-h-[400px] overflow-auto">
                  {viewOne.result ? JSON.stringify(viewOne.result, null, 2) : "(no result cached — try re-run)"}
                </pre>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2">
              <button onClick={() => rerun(viewOne)}
                disabled={busy === viewOne.id}
                className="btn-primary text-xs inline-flex items-center gap-1"
                data-testid="studies-view-rerun">
                <ArrowClockwise size={12} /> Re-run
              </button>
              <button onClick={() => exportPdf(viewOne)}
                className="btn-ghost text-xs inline-flex items-center gap-1 py-1.5"
                data-testid="studies-view-pdf">
                <FilePdf size={12} /> Export PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
